import { Box, Card, Stack } from '@mui/material';
import { PeriodTypeEnum, Timetable } from '@schooly/api';
import { SchoolUserRole } from '@schooly/constants';
import { buildTestId, TIMETABLE } from '@schooly/constants/test-ids';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { PlusIcon, PublishIcon, SimpleButton, TypographyWithOverflowHint } from '@schooly/style';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { getAgeGroupNames } from '../SchoolTimetableStructures/utils';
import { TimetableRow } from './TimetableRow';
import { SCHOOL_YEAR_ID_PARAM, STRUCTURE_ID_PARAM } from './utils';

export type TimetableStructureInfo = {
  id: string;
  name: string;
  ageGroupIds: string[];
  weekdaysCount: number;
  lessonsCount: number;
};

export type TimetableStructureSectionProps = {
  structure: TimetableStructureInfo;
  timetables: Timetable[];
  schoolId: string;
  schoolYearId: string;
  isPastYear: boolean;
  canManageTimetables: boolean;
};

export const TimetableStructureSection: FC<TimetableStructureSectionProps> = ({
  structure,
  timetables,
  schoolId,
  schoolYearId,
  isPastYear,
  canManageTimetables,
}) => {
  const { formatMessage } = useIntl();

  const { getAgeGroupById, getSchoolLevelById, schoolLevelsWithAgeGroupsMap } = useAgeGroups({
    schoolId,
    userType: SchoolUserRole.Student,
  });

  const ageGroupNames = useMemo(
    () =>
      getAgeGroupNames({
        ageGroupIds: structure.ageGroupIds,
        getAgeGroupById,
        getSchoolLevelById,
        schoolLevelsWithAgeGroupsMap,
      }),
    [structure.ageGroupIds, getAgeGroupById, getSchoolLevelById, schoolLevelsWithAgeGroupsMap],
  );

  return (
    <Card data-scope sx={{ overflow: 'visible', mb: 2, p: 2.5 }}>
      <Stack direction="row" gap={6} alignItems="center">
        <Stack minWidth={180}>
          <TypographyWithOverflowHint variant="h2">{structure.name}</TypographyWithOverflowHint>
        </Stack>

        <Stack minWidth={150}>
          <TypographyWithOverflowHint variant="h3" color="common.grey">
            {ageGroupNames ?? ''}
          </TypographyWithOverflowHint>
        </Stack>

        <Stack minWidth={150}>
          <TypographyWithOverflowHint variant="h3" color="common.grey">
            {formatMessage(
              { id: 'school-timetableStructure-DaysAndLessons' },
              { days: structure.weekdaysCount, lessons: structure.lessonsCount },
            )}
          </TypographyWithOverflowHint>
        </Stack>
      </Stack>

      <Box pt={timetables.length > 0 ? 2 : 0}>
        {timetables.map((timetable) => (
          <TimetableRow
            key={timetable.id}
            id={timetable.id}
            name={timetable.name}
            publishedAt={timetable.published_at}
            isPastYear={isPastYear}
            canManageTimetables={canManageTimetables}
          />
        ))}

        {!isPastYear && canManageTimetables && (
          <Stack direction="row" gap={2.5} mt={2}>
            <Link
              to={`/settings/timetables/new?${SCHOOL_YEAR_ID_PARAM}=${schoolYearId}&${STRUCTURE_ID_PARAM}=${structure.id}`}
              data-test-id={buildTestId(
                TIMETABLE.CREATE_LINK_PREFIX,
                { name: structure.name },
                'name',
              )}
            >
              <SimpleButton startIcon={<PlusIcon />}>
                {formatMessage({ id: 'school-timetables-CreateTimetable' })}
              </SimpleButton>
            </Link>
            <Link
              to={`/settings/timetables/import?${SCHOOL_YEAR_ID_PARAM}=${schoolYearId}&${STRUCTURE_ID_PARAM}=${structure.id}`}
              data-test-id={buildTestId(
                TIMETABLE.IMPORT_LINK_PREFIX,
                { name: structure.name },
                'name',
              )}
            >
              <SimpleButton startIcon={<PublishIcon className="reset-svg-currentColor" />}>
                {formatMessage({ id: 'school-timetables-Import' })}
              </SimpleButton>
            </Link>
          </Stack>
        )}
      </Box>
    </Card>
  );
};

export const mapStructureFromResponse = (structure: {
  id: string;
  name: string;
  age_group_ids: string[];
  weekdays: string[];
  periods: { type: PeriodTypeEnum }[];
}): TimetableStructureInfo => ({
  id: structure.id,
  name: structure.name,
  ageGroupIds: structure.age_group_ids,
  weekdaysCount: structure.weekdays.length,
  lessonsCount: structure.periods.filter((p) => p.type === PeriodTypeEnum.Lesson).length,
});
